/**
 * Dynamic Negative Prompt Engine
 * Analyzes a final prompt to extract scene characteristics, then generates
 * a context-aware negative prompt to prevent common generation artifacts.
 *
 * This is a pure additive layer — it does NOT modify any existing logic.
 */

// ─── Scene Analysis Types ───

export interface SceneProfile {
  sceneType: 'portrait' | 'landscape' | 'product' | 'action' | 'interior' | 'abstract' | 'macro' | 'general';
  styleType: 'photorealistic' | 'anime' | 'illustration' | 'cinematic' | 'product' | '3d_render' | 'oil_painting' | 'watercolor' | 'pixel_art' | 'general';
  subjectType: 'character' | 'animal' | 'product' | 'vehicle' | 'architecture' | 'food' | 'nature' | 'general';
  lightingType: 'natural' | 'studio' | 'dramatic' | 'neon' | 'low_key' | 'high_key' | 'backlit' | 'general';
  environment: 'outdoor' | 'indoor' | 'studio' | 'underwater' | 'space' | 'fantasy' | 'urban' | 'general';
  renderType: 'photo' | 'cgi' | 'hand_drawn' | 'mixed_media' | 'general';
  hasText: boolean;
  hasHands: boolean;
  hasFace: boolean;
  hasMultipleSubjects: boolean;
  isCloseUp: boolean;
  isWideShot: boolean;
}

// ─── Keyword dictionaries for classification ───

const SCENE_KEYWORDS: Record<SceneProfile['sceneType'], string[]> = {
  portrait: ['portrait', 'headshot', 'face', 'bust', 'selfie', 'close-up of person', 'character portrait'],
  landscape: ['landscape', 'scenery', 'panorama', 'vista', 'horizon', 'mountains', 'ocean', 'forest', 'valley', 'desert'],
  product: ['product', 'packshot', 'commercial', 'advertisement', 'merchandise', 'catalog', 'white background'],
  action: ['action', 'running', 'jumping', 'fighting', 'explosion', 'chase', 'dynamic motion', 'battle'],
  interior: ['interior', 'room', 'living room', 'kitchen', 'bedroom', 'office', 'hallway', 'indoor scene'],
  abstract: ['abstract', 'fractal', 'geometric', 'pattern', 'surreal', 'psychedelic', 'non-representational'],
  macro: ['macro', 'close-up', 'microscopic', 'detail shot', 'extreme close'],
  general: [],
};

const STYLE_KEYWORDS: Record<SceneProfile['styleType'], string[]> = {
  photorealistic: ['photorealistic', 'photo', 'realistic', 'hyperrealistic', 'real', 'photography', 'dslr', 'canon', 'nikon', 'sony', 'fujifilm', 'hasselblad', 'raw photo'],
  anime: ['anime', 'manga', 'cel shading', 'cel-shaded', 'japanese animation', 'studio ghibli', 'makoto shinkai'],
  illustration: ['illustration', 'illustrated', 'digital art', 'concept art', 'artstation', 'deviantart', 'drawing'],
  cinematic: ['cinematic', 'film', 'movie', 'anamorphic', 'widescreen', 'film still', 'movie scene', 'blockbuster'],
  product: ['product photography', 'commercial photography', 'catalog', 'e-commerce', 'packshot'],
  '3d_render': ['3d render', '3d', 'octane', 'unreal engine', 'blender', 'c4d', 'cinema4d', 'vray', 'ray tracing'],
  oil_painting: ['oil painting', 'oil on canvas', 'fine art painting', 'classical painting', 'impasto'],
  watercolor: ['watercolor', 'watercolour', 'aquarelle', 'wash painting'],
  pixel_art: ['pixel art', 'pixel', '8-bit', '16-bit', 'retro game'],
  general: [],
};

const SUBJECT_KEYWORDS: Record<SceneProfile['subjectType'], string[]> = {
  character: ['person', 'man', 'woman', 'girl', 'boy', 'child', 'character', 'warrior', 'knight', 'hero', 'wizard', 'soldier'],
  animal: ['animal', 'cat', 'dog', 'bird', 'horse', 'dragon', 'wolf', 'lion', 'eagle', 'creature', 'beast'],
  product: ['product', 'bottle', 'shoe', 'watch', 'phone', 'gadget', 'package', 'box', 'perfume'],
  vehicle: ['car', 'vehicle', 'motorcycle', 'spaceship', 'aircraft', 'ship', 'train', 'bicycle'],
  architecture: ['building', 'architecture', 'cathedral', 'castle', 'skyscraper', 'temple', 'bridge', 'tower'],
  food: ['food', 'dish', 'meal', 'cake', 'pizza', 'sushi', 'dessert', 'fruit', 'coffee'],
  nature: ['flower', 'tree', 'plant', 'leaf', 'forest', 'waterfall', 'river', 'mountain', 'sunset'],
  general: [],
};

const LIGHTING_KEYWORDS: Record<SceneProfile['lightingType'], string[]> = {
  natural: ['natural light', 'sunlight', 'daylight', 'golden hour', 'blue hour', 'overcast'],
  studio: ['studio light', 'softbox', 'strobe', 'flash', 'key light', 'fill light', 'rim light'],
  dramatic: ['dramatic light', 'chiaroscuro', 'rembrandt', 'high contrast', 'shadow play', 'moody'],
  neon: ['neon', 'neon light', 'cyberpunk', 'rgb', 'fluorescent', 'glow'],
  low_key: ['low key', 'dark', 'shadow', 'noir', 'silhouette'],
  high_key: ['high key', 'bright', 'overexposed', 'white', 'airy'],
  backlit: ['backlit', 'backlighting', 'contre-jour', 'lens flare', 'halo'],
  general: [],
};

const ENV_KEYWORDS: Record<SceneProfile['environment'], string[]> = {
  outdoor: ['outdoor', 'outside', 'field', 'garden', 'street', 'park', 'beach', 'mountain'],
  indoor: ['indoor', 'inside', 'room', 'interior', 'hall', 'corridor'],
  studio: ['studio', 'backdrop', 'seamless background', 'white background', 'green screen'],
  underwater: ['underwater', 'ocean', 'deep sea', 'aquatic', 'coral reef'],
  space: ['space', 'cosmos', 'galaxy', 'nebula', 'planet', 'stars', 'asteroid'],
  fantasy: ['fantasy', 'magical', 'enchanted', 'mystical', 'fairy tale', 'otherworldly'],
  urban: ['urban', 'city', 'street', 'downtown', 'metropolis', 'alley', 'rooftop'],
  general: [],
};

// ─── Classification ───

function classify<T extends string>(text: string, dict: Record<T, string[]>, fallback: T): T {
  const lower = text.toLowerCase();
  let best: T = fallback;
  let bestScore = 0;
  for (const [key, keywords] of Object.entries(dict) as [T, string[]][]) {
    if (key === fallback) continue;
    let score = 0;
    for (const kw of keywords) {
      if (lower.includes(kw)) score += kw.split(' ').length; // multi-word matches score higher
    }
    if (score > bestScore) { bestScore = score; best = key; }
  }
  return best;
}

export function analyzeScene(prompt: string): SceneProfile {
  const lower = prompt.toLowerCase();
  return {
    sceneType: classify(lower, SCENE_KEYWORDS, 'general'),
    styleType: classify(lower, STYLE_KEYWORDS, 'general'),
    subjectType: classify(lower, SUBJECT_KEYWORDS, 'general'),
    lightingType: classify(lower, LIGHTING_KEYWORDS, 'general'),
    environment: classify(lower, ENV_KEYWORDS, 'general'),
    renderType: /3d|octane|unreal|blender|c4d|vray/i.test(lower) ? 'cgi'
      : /oil paint|watercolor|sketch|pencil|ink/i.test(lower) ? 'hand_drawn'
      : /photo|dslr|canon|nikon|sony/i.test(lower) ? 'photo' : 'general',
    hasText: /text|lettering|typography|writing|caption|title|logo/i.test(lower),
    hasHands: /hand|hands|fingers|grip|holding|grasping/i.test(lower),
    hasFace: /face|portrait|headshot|expression|eyes|smile/i.test(lower),
    hasMultipleSubjects: /group|crowd|couple|team|duo|trio|people|characters/i.test(lower),
    isCloseUp: /close-up|closeup|close up|macro|extreme close|headshot/i.test(lower),
    isWideShot: /wide shot|panorama|wide angle|ultra-wide|establishing shot/i.test(lower),
  };
}

// ─── Negative Prompt Generation ───

const UNIVERSAL_NEGATIVES = [
  'worst quality', 'low quality', 'blurry', 'jpeg artifacts', 'compression artifacts',
  'watermark', 'signature', 'username', 'artist name',
];

const STYLE_EXCLUSIONS: Record<SceneProfile['styleType'], string[]> = {
  photorealistic: ['cartoon', 'anime', 'illustration', 'drawing', 'sketch', 'painting', 'cel shading', '3d render', 'cgi', 'digital art', 'unrealistic proportions'],
  anime: ['photorealistic', 'photo', 'realistic skin', 'wrinkles', 'pores', 'realistic texture', '3d render'],
  illustration: ['photorealistic', 'photo', 'raw photo', 'realistic', 'film grain', '3d render'],
  cinematic: ['cartoon', 'anime', 'illustration', 'flat lighting', 'flash photography', 'overexposed', 'underexposed'],
  product: ['cluttered background', 'messy environment', 'distracting elements', 'busy background', 'people in background', 'shadows on product'],
  '3d_render': ['photorealistic skin', 'film grain', 'lens distortion', 'chromatic aberration', 'photo noise', '2d flat'],
  oil_painting: ['digital art', 'photorealistic', 'smooth render', '3d', 'anime', 'pixel art'],
  watercolor: ['sharp edges', 'photorealistic', '3d render', 'digital art', 'oil painting', 'hard lines'],
  pixel_art: ['smooth gradients', 'photorealistic', 'high resolution textures', 'anti-aliasing', 'blur', '3d render'],
  general: [],
};

const SUBJECT_NEGATIVES: Record<SceneProfile['subjectType'], string[]> = {
  character: ['deformed', 'ugly', 'mutilated', 'disfigured', 'bad anatomy', 'wrong proportions', 'extra limbs', 'cloned face', 'gross proportions', 'malformed limbs', 'missing arms', 'missing legs', 'extra arms', 'extra legs', 'fused fingers', 'too many fingers'],
  animal: ['deformed', 'mutilated', 'extra legs', 'extra heads', 'wrong anatomy', 'unnatural pose', 'human features on animal'],
  product: ['damaged', 'scratched', 'dirty', 'fingerprints', 'dust', 'imperfections on product', 'warped shape', 'wrong perspective'],
  vehicle: ['deformed', 'wrong proportions', 'floating', 'broken parts', 'misaligned wheels'],
  architecture: ['collapsed', 'broken', 'impossible geometry', 'floating elements', 'wrong perspective', 'warped lines'],
  food: ['rotten', 'moldy', 'unappetizing', 'raw when should be cooked', 'insects', 'hair'],
  nature: ['dead plants', 'wilting', 'pollution', 'trash', 'artificial elements'],
  general: [],
};

const SCENE_NEGATIVES: Record<SceneProfile['sceneType'], string[]> = {
  portrait: ['bad face', 'asymmetric eyes', 'crossed eyes', 'uneven eyes', 'distorted face', 'long neck', 'bad teeth'],
  landscape: ['people', 'text', 'buildings in wrong place', 'floating objects', 'wrong scale'],
  product: ['cluttered', 'busy background', 'shadows on background', 'reflections of photographer'],
  action: ['static pose', 'stiff', 'frozen', 'no sense of motion'],
  interior: ['impossible architecture', 'wrong perspective', 'floating furniture', 'clipping'],
  abstract: ['realistic elements', 'faces', 'text', 'recognizable objects'],
  macro: ['out of focus subject', 'busy background', 'wrong scale'],
  general: [],
};

const LIGHTING_NEGATIVES: Record<SceneProfile['lightingType'], string[]> = {
  natural: ['artificial light', 'neon', 'studio lighting visible', 'flash'],
  studio: ['natural scenery', 'outdoor background', 'uncontrolled shadows'],
  dramatic: ['flat lighting', 'overexposed', 'no shadows', 'even lighting'],
  neon: ['natural daylight', 'warm tones', 'soft natural light'],
  low_key: ['bright', 'overexposed', 'high key', 'washed out'],
  high_key: ['dark', 'moody', 'heavy shadows', 'underexposed'],
  backlit: ['flat front lighting', 'no depth', 'harsh direct light'],
  general: [],
};

export function generateNegativePrompt(finalPrompt: string): string {
  if (!finalPrompt.trim()) return '';

  const profile = analyzeScene(finalPrompt);
  const parts: string[] = [...UNIVERSAL_NEGATIVES];

  // Style exclusions
  parts.push(...(STYLE_EXCLUSIONS[profile.styleType] || []));

  // Subject-specific negatives
  parts.push(...(SUBJECT_NEGATIVES[profile.subjectType] || []));

  // Scene-specific negatives
  parts.push(...(SCENE_NEGATIVES[profile.sceneType] || []));

  // Lighting-specific negatives
  parts.push(...(LIGHTING_NEGATIVES[profile.lightingType] || []));

  // Conditional negatives based on detected features
  if (profile.hasHands) {
    parts.push('extra fingers', 'mutated hands', 'poorly drawn hands', 'missing fingers', 'fused fingers', 'too many fingers', 'six fingers');
  }
  if (profile.hasFace) {
    parts.push('deformed iris', 'deformed pupils', 'poorly drawn face', 'cloned face', 'poorly drawn eyes');
  }
  if (profile.hasText) {
    parts.push('misspelled text', 'garbled text', 'unreadable text', 'wrong letters', 'gibberish text');
  }
  if (profile.hasMultipleSubjects) {
    parts.push('merged bodies', 'conjoined', 'overlapping faces', 'clone artifacts');
  }
  if (profile.isCloseUp) {
    parts.push('cropped frame', 'missing top of head', 'cut off chin');
  }
  if (profile.isWideShot) {
    parts.push('too close', 'cropped', 'tight framing');
  }

  // Environment-specific
  if (profile.environment === 'studio') {
    parts.push('outdoor elements', 'weather', 'natural scenery behind');
  }
  if (profile.environment === 'underwater') {
    parts.push('dry', 'above water', 'no water effects');
  }
  if (profile.environment === 'space') {
    parts.push('gravity', 'ground', 'earthly landscape', 'atmosphere');
  }

  // Deduplicate
  const unique = [...new Set(parts.map(p => p.toLowerCase().trim()).filter(Boolean))];
  return unique.join(', ');
}
